import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAdminSession } from '../adminSession';
import {
  type AdminDocument,
  type CreateDocumentPayload,
  createDocument,
  deleteDocument,
  fetchDocumentList,
  getDocumentDownloadUrl,
  patchDocument,
} from './adminDocumentApi';
import './AdminDocumentManage.scss';

const DOCUMENT_CATEGORIES = [
  '계약/협약',
  '사내 규정',
  '업무 가이드',
  '보고서',
  '양식/서식',
  '기타',
] as const;

const FILTER_CATEGORIES = ['ALL', ...DOCUMENT_CATEGORIES] as const;

type ViewMode = 'list' | 'create' | 'edit';

function getTodayDateString() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function createEmptyForm(): CreateDocumentPayload {
  return {
    title: '',
    description: '',
    category: DOCUMENT_CATEGORIES[0],
    document_date: getTodayDateString(),
  };
}

function formatDateOnly(value?: string | null) {
  if (!value) return '—';
  const normalized = String(value).replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function resolveDocumentDate(row: AdminDocument) {
  if (row.document_date) return row.document_date;
  if (!row.created_at) return getTodayDateString();
  return String(row.created_at).slice(0, 10);
}

export default function AdminDocumentManage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [documents, setDocuments] = useState<AdminDocument[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] =
    useState<(typeof FILTER_CATEGORIES)[number]>('ALL');
  const [form, setForm] = useState<CreateDocumentPayload>(createEmptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingFileName, setEditingFileName] = useState<string | null>(null);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchDocumentList({
        q: appliedSearch.trim() || undefined,
        category: categoryFilter,
      });
      setDocuments(list);
    } catch (err) {
      setDocuments([]);
      setError(err instanceof Error ? err.message : '문서 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, categoryFilter]);

  useEffect(() => {
    if (viewMode !== 'list') return;
    void loadDocuments();
  }, [viewMode, loadDocuments]);

  const resetForm = () => {
    setForm(createEmptyForm());
    setEditingId(null);
    setEditingFileName(null);
    setAttachmentFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const goToList = () => {
    resetForm();
    setViewMode('list');
  };

  const openCreatePage = () => {
    resetForm();
    setViewMode('create');
  };

  const openEditPage = (row: AdminDocument) => {
    setEditingId(row.id);
    setEditingFileName(row.file_name);
    setAttachmentFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setForm({
      title: row.title,
      description: row.description || '',
      category: row.category,
      document_date: resolveDocumentDate(row),
    });
    setViewMode('edit');
  };

  const handleSearch = () => {
    setAppliedSearch(searchQuery);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const title = String(form.title || '').trim();
    const category = String(form.category || '').trim();

    if (!title) {
      alert('제목을 입력해 주세요.');
      return;
    }
    if (!category) {
      alert('카테고리를 선택해 주세요.');
      return;
    }
    if (viewMode === 'create' && !attachmentFile) {
      alert('업로드할 파일을 선택해 주세요.');
      return;
    }

    const documentDate = String(form.document_date || '').trim();
    if (!documentDate) {
      alert('날짜를 선택해 주세요.');
      return;
    }

    const payload: CreateDocumentPayload = {
      title,
      category,
      description: String(form.description || '').trim(),
      document_date: documentDate,
    };

    const session = getAdminSession();
    const options = {
      uploaderEmail: session?.email ?? sessionStorage.getItem('user') ?? '',
      uploaderName: session?.name ?? '',
    };

    setSubmitting(true);
    setError(null);
    try {
      if (viewMode === 'edit' && editingId != null) {
        await patchDocument(editingId, payload, attachmentFile ?? undefined, options);
        alert('문서가 수정되었습니다.');
      } else {
        await createDocument(payload, attachmentFile!, options);
        alert('문서가 등록되었습니다.');
      }
      goToList();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : viewMode === 'edit'
            ? '문서 수정에 실패했습니다.'
            : '문서 등록에 실패했습니다.';
      setError(message);
      alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row: AdminDocument) => {
    const ok = window.confirm(`「${row.title}」 문서를 삭제하시겠습니까?`);
    if (!ok) return;

    setDeletingId(row.id);
    setError(null);
    try {
      await deleteDocument(row.id);
      if (editingId === row.id) {
        goToList();
      }
      await loadDocuments();
    } catch (err) {
      const message = err instanceof Error ? err.message : '문서 삭제에 실패했습니다.';
      setError(message);
      alert(message);
    } finally {
      setDeletingId(null);
    }
  };

  const attachmentLabel = useMemo(() => {
    if (attachmentFile) return attachmentFile.name;
    if (editingFileName) return `현재: ${editingFileName}`;
    return '선택된 파일 없음';
  }, [attachmentFile, editingFileName]);

  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <div className="admin-document-manage admin-document-manage--form-page">
        <header className="admin-document-manage__form-header">
          <button
            type="button"
            className="admin-document-manage__back-btn"
            onClick={goToList}
            disabled={submitting}
          >
            ← 목록으로
          </button>
          <div>
            <h2 className="admin-document-manage__title">
              {viewMode === 'edit' ? '문서 수정' : '글 작성'}
            </h2>
            <p className="admin-document-manage__subtitle">
              {viewMode === 'edit'
                ? '등록된 문서 정보와 첨부파일을 수정할 수 있습니다.'
                : '새 문서를 등록합니다. 제목, 날짜, 카테고리, 첨부파일을 입력해 주세요.'}
            </p>
          </div>
        </header>

        {error ? <p className="admin-document-manage__error">{error}</p> : null}

        <section className="admin-document-manage__panel admin-document-manage__panel--form">
          <form onSubmit={handleSubmit}>
            <div className="admin-document-manage__form-grid">
              <div className="admin-document-manage__field admin-document-manage__field--wide">
                <label htmlFor="document-title">제목</label>
                <input
                  id="document-title"
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="문서 제목을 입력하세요"
                />
              </div>

              <div className="admin-document-manage__field">
                <label htmlFor="document-date">날짜</label>
                <input
                  id="document-date"
                  type="date"
                  value={form.document_date || ''}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, document_date: e.target.value }))
                  }
                />
              </div>

              <div className="admin-document-manage__field admin-document-manage__field--wide">
                <span className="admin-document-manage__field-label">카테고리</span>
                <div className="admin-document-manage__chips" role="group" aria-label="카테고리 선택">
                  {DOCUMENT_CATEGORIES.map((category) => (
                    <button
                      key={category}
                      type="button"
                      className={`admin-document-manage__chip${
                        form.category === category ? ' is-selected' : ''
                      }`}
                      onClick={() => setForm((prev) => ({ ...prev, category }))}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              <div className="admin-document-manage__field admin-document-manage__field--wide">
                <label htmlFor="document-description">설명</label>
                <textarea
                  id="document-description"
                  value={form.description || ''}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="문서 설명을 입력하세요."
                />
              </div>

              <div className="admin-document-manage__field admin-document-manage__field--wide">
                <span className="admin-document-manage__field-label">
                  첨부파일 {viewMode === 'edit' ? '(선택 · 교체 시에만 선택)' : '(필수)'} · 최대 50MB
                </span>
                <div className="admin-document-manage__file-upload">
                  <input
                    id="document-file"
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp,.hwpx,.zip,.txt,.csv,image/*"
                    onChange={(e) => setAttachmentFile(e.target.files?.[0] ?? null)}
                    className="admin-document-manage__file-input"
                  />
                  <label htmlFor="document-file" className="admin-document-manage__file-btn">
                    파일 선택
                  </label>
                  <span className="admin-document-manage__file-name">{attachmentLabel}</span>
                </div>
                <span className="admin-document-manage__file-hint">
                  PDF, Office, HWP, 이미지, ZIP, TXT, CSV 파일을 업로드할 수 있습니다.
                </span>
              </div>
            </div>

            <div className="admin-document-manage__form-actions">
              <button
                type="button"
                className="admin-document-manage__cancel-btn"
                onClick={goToList}
                disabled={submitting}
              >
                취소
              </button>
              <button
                type="submit"
                className="admin-document-manage__submit-btn"
                disabled={submitting}
              >
                {submitting
                  ? viewMode === 'edit'
                    ? '수정 중…'
                    : '등록 중…'
                  : viewMode === 'edit'
                    ? '문서 수정'
                    : '문서 등록'}
              </button>
            </div>
          </form>
        </section>
      </div>
    );
  }

  return (
    <div className="admin-document-manage admin-document-manage--list-page">
      <header className="admin-document-manage__header">
        <div>
          <h2 className="admin-document-manage__title">문서 관리</h2>
          <p className="admin-document-manage__subtitle">
            회사 업무 문서를 게시판 형식으로 등록하고 다운로드할 수 있습니다.
          </p>
        </div>
        <button
          type="button"
          className="admin-document-manage__refresh-btn"
          onClick={() => void loadDocuments()}
          disabled={loading}
        >
          {loading ? '불러오는 중…' : '새로고침'}
        </button>
      </header>

      {error ? <p className="admin-document-manage__error">{error}</p> : null}

      <section className="admin-document-manage__panel">
        <div className="admin-document-manage__board-head">
          <h3 className="admin-document-manage__section-title">문서 게시판</h3>
          <button
            type="button"
            className="admin-document-manage__create-btn"
            onClick={openCreatePage}
          >
            글생성
          </button>
        </div>

        <div className="admin-document-manage__toolbar">
          <input
            className="admin-document-manage__search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            placeholder="제목, 설명, 파일명 검색"
          />
          <button
            type="button"
            className="admin-document-manage__search-btn"
            onClick={handleSearch}
          >
            검색
          </button>
          <select
            className="admin-document-manage__filter-select"
            value={categoryFilter}
            onChange={(e) =>
              setCategoryFilter(e.target.value as (typeof FILTER_CATEGORIES)[number])
            }
          >
            <option value="ALL">전체 카테고리</option>
            {DOCUMENT_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="admin-document-manage__empty">불러오는 중…</p>
        ) : documents.length === 0 ? (
          <p className="admin-document-manage__empty">
            {appliedSearch.trim() || categoryFilter !== 'ALL'
              ? '검색 결과가 없습니다.'
              : '등록된 문서가 없습니다. 글생성 버튼으로 문서를 등록해 보세요.'}
          </p>
        ) : (
          <div className="admin-document-manage__table-wrap">
            <table className="admin-document-manage__table">
              <thead>
                <tr>
                  <th>번호</th>
                  <th>제목</th>
                  <th>카테고리</th>
                  <th>설명</th>
                  <th>등록자</th>
                  <th>날짜</th>
                  <th>용량</th>
                  <th>다운로드</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td className="admin-document-manage__title-cell">{row.title}</td>
                    <td>
                      <span className="admin-document-manage__category-badge">
                        {row.category}
                      </span>
                    </td>
                    <td className="admin-document-manage__desc-cell">
                      {row.description || '—'}
                    </td>
                    <td>{row.uploader_name || row.uploader_email || '—'}</td>
                    <td>{formatDateOnly(row.document_date || row.created_at)}</td>
                    <td>{row.file_size_label}</td>
                    <td>
                      <a
                        href={getDocumentDownloadUrl(row.file_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="admin-document-manage__download-link"
                        download={row.file_name}
                      >
                        {row.file_name}
                      </a>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="admin-document-manage__action-btn"
                        onClick={() => openEditPage(row)}
                        disabled={submitting && editingId === row.id}
                      >
                        수정
                      </button>
                      <button
                        type="button"
                        className="admin-document-manage__action-btn admin-document-manage__action-btn--danger"
                        onClick={() => void handleDelete(row)}
                        disabled={deletingId === row.id}
                      >
                        {deletingId === row.id ? '삭제 중' : '삭제'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
